import { orders } from './orders';
import { stores, getStoreById } from './stores';

// Delivery statuses
const DELIVERY_STATUSES = ['Delivered', 'In Transit', 'Out for Delivery', 'Pending', 'Failed'];
const SLOT_WINDOWS = ['9-12', '12-15', '15-18', '18-21'];
const FAILURE_REASONS = ['Customer Unavailable', 'Wrong Address', 'Refused Delivery', 'Area Not Serviceable'];

// Generate deliveries from orders
function generateDeliveries() {
    const deliveries = [];

    const deliverableOrders = orders.filter(order =>
        order.status === 'Delivered' || order.status === 'Shipped' || order.status === 'Processing'
    );

    deliverableOrders.forEach((order, index) => {
        const store = getStoreById(order.storeId);
        const delayFactor = store ? store.delayFactor : 0.1;

        // Requested slot
        const slotIndex = Math.floor(Math.random() * SLOT_WINDOWS.length);
        const requestedSlot = SLOT_WINDOWS[slotIndex];

        // Dispatch time (order time + 30-90 min)
        const orderHour = parseInt(order.time.split(':')[0]);
        const orderMin = parseInt(order.time.split(':')[1]);
        const dispatchMinutes = orderHour * 60 + orderMin + 30 + Math.floor(Math.random() * 60);
        const dispatchHour = Math.floor(dispatchMinutes / 60) % 24;
        const dispatchMin = dispatchMinutes % 60;
        const dispatchTime = `${String(dispatchHour).padStart(2, '0')}:${String(dispatchMin).padStart(2, '0')}`;

        // Estimated delivery time (45-90 min from dispatch)
        const estimatedMinutes = 45 + Math.floor(Math.random() * 45);

        // Actual delivery time (affected by store delay factor)
        const isDelayed = Math.random() < delayFactor;
        const actualMinutes = isDelayed
            ? estimatedMinutes + 15 + Math.floor(Math.random() * 30)
            : estimatedMinutes - 5 + Math.floor(Math.random() * 15);

        const actualDeliveryMinutes = dispatchMinutes + actualMinutes;
        const actualDeliveryHour = Math.floor(actualDeliveryMinutes / 60) % 24;
        const actualDeliveryMin = actualDeliveryMinutes % 60;
        const deliveryTime = `${String(actualDeliveryHour).padStart(2, '0')}:${String(actualDeliveryMin).padStart(2, '0')}`;

        // Determine if within slot
        const slotStart = parseInt(requestedSlot.split('-')[0]);
        const slotEnd = parseInt(requestedSlot.split('-')[1]);
        const isWithinSlot = actualDeliveryHour >= slotStart && actualDeliveryHour < slotEnd;

        // Delivery status based on order status
        let status;
        let failureReason = null;

        if (order.status === 'Delivered') {
            if (Math.random() < 0.02) { // 2% failed after attempt
                status = 'Failed';
                failureReason = FAILURE_REASONS[Math.floor(Math.random() * FAILURE_REASONS.length)];
            } else {
                status = 'Delivered';
            }
        } else if (order.status === 'Shipped') {
            status = Math.random() < 0.6 ? 'Out for Delivery' : 'In Transit';
        } else {
            status = 'Pending';
        }

        // Distance (2-12 km)
        const distance = (2 + Math.random() * 10).toFixed(1);

        deliveries.push({
            id: `DEL-${String(index + 1).padStart(6, '0')}`,
            orderId: order.id,
            customerId: order.customerId,
            storeId: order.storeId,
            storeName: order.storeName,
            cityId: order.cityId,
            pincode: order.pincode,
            date: order.date,
            requestedSlot,
            dispatchTime,
            estimatedMinutes,
            actualMinutes: status === 'Delivered' ? actualMinutes : null,
            deliveryTime: status === 'Delivered' ? deliveryTime : null,
            status,
            isWithinSlot: status === 'Delivered' ? isWithinSlot : null,
            isDelayed: status === 'Delivered' ? isDelayed : null,
            distance: parseFloat(distance),
            failureReason,
        });
    });

    return deliveries;
}

export const deliveries = generateDeliveries();

// Helper functions
export function getDeliveriesByDateRange(startDate, endDate) {
    return deliveries.filter(del => del.date >= startDate && del.date <= endDate);
}

export function getDeliveriesByStore(storeId) {
    return deliveries.filter(del => del.storeId === storeId);
}

export function getOnTimeDeliveries(deliveryList = deliveries) {
    return deliveryList.filter(del => del.status === 'Delivered' && del.isWithinSlot);
}

export function getDelayedDeliveries(deliveryList = deliveries) {
    return deliveryList.filter(del => del.status === 'Delivered' && del.isDelayed);
}

export function getFailedDeliveries(deliveryList = deliveries) {
    return deliveryList.filter(del => del.status === 'Failed');
}

// Statistics
export const deliveryStats = {
    get totalDeliveries() { return deliveries.length; },
    get delivered() { return deliveries.filter(d => d.status === 'Delivered').length; },
    get inTransit() { return deliveries.filter(d => d.status === 'In Transit').length; },
    get outForDelivery() { return deliveries.filter(d => d.status === 'Out for Delivery').length; },
    get pending() { return deliveries.filter(d => d.status === 'Pending').length; },
    get failed() { return deliveries.filter(d => d.status === 'Failed').length; },
    get onTimeRate() {
        const delivered = deliveries.filter(d => d.status === 'Delivered');
        if (delivered.length === 0) return 0;
        return Math.round((getOnTimeDeliveries().length / delivered.length) * 100);
    },
    get avgDeliveryTime() {
        const delivered = deliveries.filter(d => d.status === 'Delivered' && d.actualMinutes);
        if (delivered.length === 0) return 0;
        return Math.round(delivered.reduce((sum, d) => sum + d.actualMinutes, 0) / delivered.length);
    },
};

export default deliveries;
